import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Tooltip,
  Button,
  Rating,
} from "@mui/material";
import { Loader } from "components/Loader";
import {
  AddCircleOutline,
  RemoveCircleOutline,
  Edit as EditIcon,
  DoubleArrow,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { themes } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";

import {
  CustomButtonWithOutLoader,
  CustomButton,
} from "components/CustomComponents/CustomButton";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";

const timeToMinutes = (timeStr) => {
  if (!timeStr || !timeStr.includes(":")) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
};

const EODViewPanel = ({
  onClose,
  reportId,
  reCallApi,
  hasProjectAccess,
  viewMode = "my_zone",
}) => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const isTeamMode = viewMode === "team_zone";

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [autoPopulateFailed, setAutoPopulateFailed] = useState(false);

  const [isPlanEditMode, setIsPlanEditMode] = useState(false);
  const [originalPlanData, setOriginalPlanData] = useState([]);
  const [planSaveLoading, setPlanSaveLoading] = useState(false);
  const [isAchievedRefreshed, setIsAchievedRefreshed] = useState(false);

  const getIsPastDate = (date) => {
    if (!date) return false;
    const parsed = dayjs(date, "DD-MM-YYYY", true);
    if (!parsed.isValid()) return false;
    return parsed.isBefore(dayjs(), "day");
  };

  const isPastDate = getIsPastDate(reportData?.report_date);

  const [managerInputs, setManagerInputs] = useState({
    rating: 0,
    comments: "",
    reopenReason: "",
  });
  const [showReopenField, setShowReopenField] = useState(false);

  const isSubmitted =
    (reportData &&
      (reportData.status === "Submitted" ||
        reportData.is_submitted ||
        reportData.status === "Approved" ||
        reportData.status === "Missed")) ||
    false;

  const [formData, setFormData] = useState({
    report_date: "",
    total_worked_hours: "",
    planned_tasks_today: [],
    achieved_tasks: [],
    next_session_tasks: [],
    blockers_notes: "",
  });

  const [projectTaskList, setProjectTaskList] = useState([]);

  const isWorkedHoursInvalid =
    timeToMinutes(formData.total_worked_hours) > timeToMinutes("17:00");

  const fetchReportDetails = useCallback(async () => {
    if (!domain || !reportId) return;
    setAutoPopulateFailed(false);
    setLoading(true);
    try {
      const res = await customGetAPI(`${domain}eod-reports/${reportId}/`);

      if (res.ok) {
        const data = res.responseDetails || res.data;

        let plannedForToday = data.tasks_planned_for_today_actual || [];
        let achievedTasks = data.achieved_tasks || [];
        let totalWorkedHours = data.total_worked_hours || "00:00";

        const parsedDate = dayjs(data.report_date, "DD-MM-YYYY", true);

        if (!parsedDate.isValid()) {
          console.error("Invalid report date:", data.report_date);
          return;
        }

        const formattedDate = parsedDate.format("YYYY-MM-DD");

        if (
          (data.status === "Missed" || data.status === "Draft") &&
          plannedForToday.length === 0
        ) {
          try {
            const plannedRes = await customGetAPI(
              `${domain}eod-reports/planned_for_date/?user_id=${data.user_id}&date=${formattedDate}`,
            );
            if (
              plannedRes.ok ||
              (plannedRes.data && plannedRes.data.planned_tasks)
            ) {
              const pData =
                plannedRes.responseDetails || plannedRes.data || plannedRes;
              if (pData.planned_tasks) {
                plannedForToday = pData.planned_tasks;
              }
            }
          } catch (err) {
            console.error(
              "Failed to auto-fetch planned tasks for missed report",
              err,
            );
          }
        }

        setReportData(data);

        setManagerInputs((prev) => ({
          ...prev,
          rating: parseInt(data.ratings) || 0,
          comments: data.approver_comments || "",
        }));

        setFormData({
          report_date: data.report_date,
          total_worked_hours: totalWorkedHours,
          blockers_notes: data.blockers_notes || "",

          planned_tasks_today: plannedForToday.map((t) => ({
            task_id: t.task_id,
            task_no: t.task_no || "-",
            task_name: t.task_name || "",
            planned_hours: t.planned_hours
              ? t.planned_hours.substring(0, 5)
              : "",
            remaining_eta: t.remaining_eta || "",
          })),

          achieved_tasks: achievedTasks.map((t) => ({
            id: t.id,
            task_id: t.task_id,
            task_no: t.task_no || "-",
            task_name: t.task_name || "",
            worked_hours: t.worked_hours || "00:00",
            is_manual: t.is_manual_entry,
          })),

          next_session_tasks: (data.tasks_planning_for_tomorrow || []).map(
            (t) => ({
              id: t.id,
              task_id: t.task_id,
              task_no: t.task_no || "-",
              task_name: t.task_name || "",
              remaining_eta: t.remaining_eta || "00:00",
              planned_hours: t.planned_hours
                ? t.planned_hours.substring(0, 5)
                : "00:00",
              sequence_order: t.sequence_order,
            }),
          ),
        });
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      showToast("error", "Failed to load report details");
    } finally {
      setLoading(false);
    }
  }, [domain, reportId]);

  const fetchAvailableTasks = useCallback(async () => {
    if (!hasProjectAccess || !userDetails?.id) return;
    try {
      const res = await customGetAPI(
        `${domain}eod-reports/available_tasks/?user_id=${userDetails.id}`,
      );
      if (res.ok) {
        const tasks = Array.isArray(res.responseDetails)
          ? res.responseDetails
          : res.data || [];
        setProjectTaskList(
          tasks.map((t) => ({
            task_id: t.id,
            task_no: t.task_code,
            task_name: t.taskname,
            remaining_eta: t.remaining_eta || "00:00",
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching tasks", error);
    }
  }, [domain, userDetails.id, hasProjectAccess]);

  useEffect(() => {
    fetchReportDetails();
  }, [fetchReportDetails]);

  useEffect(() => {
    if ((isEditMode || isPlanEditMode) && hasProjectAccess && !isTeamMode) {
      fetchAvailableTasks();
    }
  }, [
    isEditMode,
    isPlanEditMode,
    hasProjectAccess,
    isTeamMode,
    fetchAvailableTasks,
  ]);

  const handlePlanEditToggle = () => {
    if (!isPlanEditMode) {
      setOriginalPlanData([...formData.planned_tasks_today]);
      setIsPlanEditMode(true);
    } else {
      setFormData((prev) => ({
        ...prev,
        planned_tasks_today: originalPlanData,
      }));
      setIsPlanEditMode(false);
    }
  };

  const updatePlanRow = (index, field, value) => {
    const newList = [...formData.planned_tasks_today];
    newList[index] = { ...newList[index], [field]: value };
    setFormData((prev) => ({ ...prev, planned_tasks_today: newList }));
  };

  const addPlanRow = () => {
    const newItem = {
      task_id: null,
      task_no: "",
      task_name: "",
      planned_hours: "",
      remaining_eta: "",
    };
    setFormData((prev) => ({
      ...prev,
      planned_tasks_today: [...prev.planned_tasks_today, newItem],
    }));
  };

  const removePlanRow = (index) => {
    const newList = [...formData.planned_tasks_today];
    newList.splice(index, 1);
    setFormData((prev) => ({ ...prev, planned_tasks_today: newList }));
  };

  const handleSaveCurrentPlan = async () => {
    const tasks = formData.planned_tasks_today;
    if (tasks.length === 0) {
      showToast("error", "At least one task must be planned.");
      return;
    }
    if (hasProjectAccess) {
      if (
        tasks.some(
          (t) => !t.task_id || !t.planned_hours || t.planned_hours.length < 5,
        )
      ) {
        showToast(
          "error",
          "Please select tasks and enter valid planned hours.",
        );
        return;
      }
    } else {
      if (tasks.some((t) => !t.task_name.trim())) {
        showToast("error", "Please enter task names.");
        return;
      }
    }

    setPlanSaveLoading(true);

    const payload = {
      has_project_access: hasProjectAccess,
      current_day_plan: tasks.map((t, index) => {
        const item = {
          task_name: t.task_name,
          sequence_order: index + 1,
        };
        if (hasProjectAccess) {
          item.task_id = t.task_id;
          item.planned_hours = t.planned_hours;
        }
        return item;
      }),
    };

    try {
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/update_current_day_plan/`,
        "POST",
        payload,
      );
      if (res.ok) {
        showToast("success", "Planned tasks updated successfully");
        setIsPlanEditMode(false);
        reCallApi(true);
        fetchReportDetails();
      } else {
        showToast("error", res.message || "Failed to update plan");
      }
    } catch (e) {
      showToast("error", "Error updating plan");
    } finally {
      setPlanSaveLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode) fetchReportDetails();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateRow = (listName, index, field, value) => {
    const newList = [...formData[listName]];

    if (listName === "next_session_tasks" && field === "planned_hours") {
      const task = newList[index];
      if (hasProjectAccess && task.remaining_eta && value.length === 5) {
        if (
          value !== task.remaining_eta &&
          timeToMinutes(value) > timeToMinutes(task.remaining_eta)
        ) {
          return;
        }
      }
    }
    newList[index][field] = value;
    setFormData((prev) => ({ ...prev, [listName]: newList }));
  };

  const removeRow = (listName, index) => {
    const newList = [...formData[listName]];
    newList.splice(index, 1);
    setFormData((prev) => ({ ...prev, [listName]: newList }));
  };

  const addRow = (listName) => {
    const newItem =
      listName === "achieved_tasks"
        ? { task_name: "", worked_hours: "" }
        : {
            task_id: null,
            task_no: "",
            task_name: "",
            remaining_eta: "",
            planned_hours: "",
          };
    setFormData((prev) => ({
      ...prev,
      [listName]: [...prev[listName], newItem],
    }));
  };

  const isValidTime = (timeStr) => {
    if (!timeStr || timeStr === "Invalid Date") return false;
    return /^([01]\d|2[0-3]):([0-5]\d)$/.test(timeStr);
  };

  const canAddNextRow = (list, type) => {
    if (list.length === 0) return true;
    const lastItem = list[list.length - 1];
    if (!lastItem.task_name || !lastItem.task_name.trim()) return false;
    if (type === "project_planned") {
      if (!lastItem.task_id) return false;
      if (!isValidTime(lastItem.planned_hours)) return false;
      if (!lastItem.planned_hours || lastItem.planned_hours.length < 5)
        return false;
    }
    return true;
  };

  const isSaveDisabled = () => {
    if (timeToMinutes(formData.total_worked_hours) > timeToMinutes("17:00")) {
      return true;
    }
    if (!hasProjectAccess) {
      if (
        !formData.total_worked_hours ||
        formData.total_worked_hours === "Invalid Date"
      )
        return true;
      if (formData.achieved_tasks.length === 0) return true;
      if (formData.achieved_tasks.some((t) => !t.task_name.trim())) return true;
    }
    if (formData.next_session_tasks.length === 0) return true;
    if (hasProjectAccess) {
      if (
        formData.next_session_tasks.some(
          (t) =>
            !t.task_id ||
            !isValidTime(t.planned_hours) ||
            !t.planned_hours ||
            t.planned_hours.length < 5,
        )
      )
        return true;

      const hasExceededTime = formData.next_session_tasks.some(
        (t) =>
          t.remaining_eta &&
          timeToMinutes(t.planned_hours) > timeToMinutes(t.remaining_eta),
      );
      if (hasExceededTime) return true;
    } else {
      if (formData.next_session_tasks.some((t) => !t.task_name.trim()))
        return true;
    }
    return false;
  };

  const handleUpdate = async () => {
    if (isSaveDisabled()) {
      showToast("warning", "Please fill all required fields correctly.");
      return;
    }
    setLoading(true);
    let payload = {
      user_id: userDetails.id,
      report_date: formData.report_date,
      has_project_access: hasProjectAccess,
      blockers_notes: formData.blockers_notes,
    };

    if (hasProjectAccess) {
      payload.planned_tasks = formData.next_session_tasks.map((t, index) => ({
        task_id: t.task_id,
        planned_hours:
          t.planned_hours.length === 5
            ? `${t.planned_hours}:00`
            : t.planned_hours,
        sequence_order: index + 1,
      }));
      if (isAchievedRefreshed) {
        payload.achieved_tasks = formData.achieved_tasks.map((t) => ({
          task_id: t.task_id,
          worked_hours: t.worked_hours,
        }));
      }
    } else {
      payload.total_worked_hours = formData.total_worked_hours;
      payload.achieved_tasks = formData.achieved_tasks.map((t) => ({
        task_name: t.task_name,
      }));
      payload.planned_tasks = formData.next_session_tasks.map((t, index) => ({
        task_name: t.task_name,
        sequence_order: index + 1,
      }));
    }

    try {
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/`,
        "PUT",
        payload,
      );
      if (res.ok) {
        showToast("success", "Report Updated Successfully");
        setIsEditMode(false);
        reCallApi(true);
        fetchReportDetails();
      } else {
        showToast("error", res.message || "Update Failed");
      }
    } catch (err) {
      showToast("error", "Error updating report");
    }
  };

  const handleSubmitReport = async () => {
    if (!reportId) return;
    setSubmitting(true);
    try {
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/submit/`,
        "POST",
      );
      if (res.ok) {
        showToast("success", "Report submitted successfully");
        reCallApi(true);
        fetchReportDetails();
      } else {
        showToast("error", res.message || "Submit Failed");
      }
    } catch (err) {
      showToast("error", "Error submitting report");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!managerInputs.rating || !managerInputs.comments) {
      showToast("error", "Rating and Comments are required to approve.");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        approver_comments: managerInputs.comments,
        ratings: managerInputs.rating,
      };
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/approve/`,
        "PUT",
        payload,
      );
      if (res.ok) {
        showToast("success", "Report Approved Successfully");
        reCallApi(true);
        onClose();
      } else {
        showToast("error", res.message || "Approval Failed");
      }
    } catch (e) {
      showToast("error", "Error approving report");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async () => {
    if (!managerInputs.reopenReason) {
      showToast("error", "Please provide a reason for reopening.");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        reason: managerInputs.reopenReason,
        reopened_by_user_id: userDetails.id,
      };
      const res = await customAPI(
        `${domain}eod-reports/${reportId}/reopen/`,
        "POST",
        payload,
      );
      if (res.ok) {
        showToast("success", "Report Reopened Successfully");
        reCallApi(true);
        onClose();
      } else {
        showToast("error", res.message || "Reopen Failed");
      }
    } catch (e) {
      showToast("error", "Error reopening report");
    } finally {
      setActionLoading(false);
    }
  };

  const formatReopenHistory = (text) => {
    if (!text) return [];

    return text
      .split(/\[REOPENED on/g)
      .filter(Boolean)
      .map((entry) => `[REOPENED on ${entry.trim()}`);
  };

  const styles = {
    manualBox: (type) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      p: 0.5,
      mb: 1.5,
      borderRadius: "8px",
      backgroundColor: type === "planned" ? "#FFF9E6" : "#F1FDF9",
      border: `1px solid ${type === "planned" ? "#FFD700" : "#00D4AA"}`,
    }),
    boxInput: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent",
        "& fieldset": { border: "none" },
      },
      "& .MuiOutlinedInput-input": { padding: "10px", fontSize: "0.95rem" },
    },
    tableHead: (bgcolor) => ({
      backgroundColor: bgcolor,
      "& th": {
        fontWeight: "bold",
        fontSize: "0.8rem",
        color: "#333",
        borderBottom: "1px solid #ddd",
        whiteSpace: "nowrap",
        py: 1,
        px: 1,
      },
    }),
    selectInput: {
      "& .MuiOutlinedInput-root": {
        paddingTop: "2px !important",
        paddingBottom: "2px !important",
        paddingLeft: "8px !important",
        "& fieldset": { borderColor: "#c4c4c4" },
        "&:hover fieldset": { borderColor: "#000" },
      },
      "& .MuiOutlinedInput-input": {
        fontSize: "0.85rem",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      },
    },
  };

  const isPlanSaveDisabled = () => {
    if (formData.planned_tasks_today.length === 0) return true;
    if (planSaveLoading) return true;
    const hasEmptyFields = formData.planned_tasks_today.some(
      (t) => !t.task_id || !t.planned_hours || t.planned_hours.length < 5,
    );
    if (hasEmptyFields) return true;
    const hasExceededTime = formData.planned_tasks_today.some((t) => {
      if (!t.remaining_eta) return false;
      return timeToMinutes(t.planned_hours) > timeToMinutes(t.remaining_eta);
    });
    if (hasExceededTime) return true;
    return false;
  };

  const handleRefreshAchievedTasks = async () => {
    if (!domain || !userDetails?.id || !formData.report_date) return;

    setActionLoading(true);
    const parsedDate = dayjs(formData.report_date, "DD-MM-YYYY", true);
    if (!parsedDate.isValid()) {
      showToast("error", "Invalid date format");
      return;
    }
    const formattedDate = parsedDate.format("YYYY-MM-DD");

    setLoading(true);
    try {
      const res = await customAPI(
        `${domain}eod-reports/auto_populate_achieved/`,
        "POST",
        { user_id: userDetails.id, report_date: formattedDate },
      );

      if (res.ok) {
        const data = res.responseDetails || res.data;
        if (data) {
          const newAchievedTasks = (data.achieved_tasks || []).map((t) => ({
            id: t.id,
            task_id: t.task_id,
            task_no: t.task_no || "-",
            task_name: t.task_name || "",
            worked_hours: t.worked_hours || "00:00",
            is_manual: t.is_manual_entry,
          }));

          setFormData((prev) => ({
            ...prev,
            achieved_tasks: newAchievedTasks,
            total_worked_hours: data.total_worked_hours || "00:00",
          }));
          console.log(formData);
          setIsAchievedRefreshed(true);

          showToast("success", "Achieved tasks refreshed successfully");
        }
      } else {
        if (res.code === 400) setAutoPopulateFailed(true);
        showToast("error", res.message || "Failed to refresh tasks");
      }
    } catch (error) {
      console.error("Error refreshing achieved tasks:", error);
      showToast("error", "Error refreshing tasks");
      setAutoPopulateFailed(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !reportData) return <Loader />;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "#fff",
        }}
      >
        {/* HEADER */}
        <Paper
          elevation={0}
          sx={{
            borderBottom: `1px solid ${themes?.miliedLightGray}`,
            borderRadius: 0,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
                py: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  onClick={() => {
                    if (isEditMode) {
                      setIsEditMode(false);
                      setIsPlanEditMode(false);
                      fetchReportDetails();
                    } else if (isPlanEditMode) {
                      handlePlanEditToggle();
                    } else onClose();
                  }}
                  sx={{
                    color: themes?.grayColor,
                    "&:hover": { backgroundColor: themes?.whiteColor },
                  }}
                >
                  <DoubleArrow />
                </IconButton>
                <Box sx={{ ml: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {isTeamMode
                      ? "Review EOD Report"
                      : isEditMode
                        ? "Edit EOD Report"
                        : isPlanEditMode
                          ? "Edit Planned Tasks"
                          : "View EOD Report"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isTeamMode
                      ? "Approve or Reopen team member's report."
                      : isEditMode
                        ? "Update your daily work progress."
                        : isPlanEditMode
                          ? "Update current day's plan."
                          : "Details of submitted report."}
                  </Typography>
                </Box>
              </Box>

              {!isTeamMode && !isEditMode && !isPlanEditMode && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip
                    title={
                      isSubmitted
                        ? `${reportData?.status || "Submitted"} - cannot edit`
                        : "Edit"
                    }
                    arrow
                    slotProps={tooltipStyleProps}
                  >
                    <span>
                      <IconButton
                        onClick={handleEditToggle}
                        disabled={isSubmitted}
                        sx={{
                          color: themes?.greenColor,
                          backgroundColor: themes?.primary,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: themes?.primary },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>

        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <UserDetailsRow
              userDetails={{
                image_url: reportData?.image_url,
                employee_id: reportData?.employee_id,
                user_name: reportData?.user_name,
                department_name: reportData?.department,
                designation: reportData?.designation,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="body2">
              <strong>Date :</strong> {formData.report_date}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">
                <strong>Worked Hours :</strong>
              </Typography>
              {isEditMode ? (
                <TimePicker
                  value={
                    formData.total_worked_hours
                      ? dayjs(formData.total_worked_hours, "HH:mm")
                      : null
                  }
                  onChange={(newValue) => {
                    const timeStr = newValue ? newValue.format("HH:mm") : "";
                    const [hh, mm] = timeStr.split(":").map(Number);
                    if (hh > 17 || (hh === 17 && mm > 0)) {
                      showToast("error", "Max allowed time is 17:00");
                    }
                    handleChange("total_worked_hours", timeStr);
                  }}
                  ampm={false}
                  readOnly={!isEditMode || hasProjectAccess}
                  slotProps={{
                    textField: {
                      size: "small",
                      placeholder: "HH:MM",
                      sx: {
                        width: 120,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 1,
                          height: 40,
                          bgcolor:
                            !isEditMode || hasProjectAccess
                              ? "#f5f5f5"
                              : "#fff",
                        },
                      },
                      error: isWorkedHoursInvalid,
                    },
                  }}
                />
              ) : (
                <Typography variant="body2">
                  {formData.total_worked_hours || "-"}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Planned Tasks
              </Typography>
              {!isTeamMode &&
                !isSubmitted &&
                isEditMode &&
                !isPlanEditMode &&
                !isPastDate && (
                  <Tooltip
                    title="Edit Plan"
                    arrow
                    slotProps={tooltipStyleProps}
                  >
                    <IconButton
                      size="small"
                      onClick={handlePlanEditToggle}
                      sx={{ color: themes.primaryButton }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              The tasks planned for this day.
            </Typography>

            {hasProjectAccess ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead sx={styles.tableHead("#FFF9E6")}>
                    <TableRow>
                      <TableCell>Task No</TableCell>
                      <TableCell width={isPlanEditMode ? "50%" : "auto"}>
                        Task Name
                      </TableCell>
                      <TableCell>Planned hrs</TableCell>
                      <TableCell>Remaining ETA</TableCell>
                      {isPlanEditMode && <TableCell width="5%"></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.planned_tasks_today.length > 0 ? (
                      formData.planned_tasks_today.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {isPlanEditMode ? (
                              <Autocomplete
                                size="small"
                                options={projectTaskList.filter(
                                  (task) =>
                                    !formData.planned_tasks_today.some(
                                      (selected) =>
                                        selected.task_id === task.task_id,
                                    ) || task.task_id === row.task_id,
                                )}
                                getOptionLabel={(option) =>
                                  option.task_no || ""
                                }
                                value={
                                  projectTaskList.find(
                                    (t) => t.task_id === row.task_id,
                                  ) || null
                                }
                                onChange={(e, val) => {
                                  const updatedList = [
                                    ...formData.planned_tasks_today,
                                  ];
                                  if (val) {
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      task_id: val.task_id,
                                      task_no: val.task_no,
                                      task_name: val.task_name,
                                      remaining_eta: val.remaining_eta,
                                    };
                                  } else {
                                    updatedList[index] = {
                                      ...updatedList[index],
                                      task_id: null,
                                      task_no: "",
                                      task_name: "",
                                      remaining_eta: "",
                                    };
                                  }
                                  setFormData({
                                    ...formData,
                                    planned_tasks_today: updatedList,
                                  });
                                }}
                                renderInput={(params) => (
                                  <Tooltip
                                    title={row.task_no || ""}
                                    arrow
                                    slotProps={tooltipStyleProps}
                                  >
                                    <TextField
                                      {...params}
                                      placeholder="Select No"
                                      sx={{
                                        ...styles.selectInput,
                                        minWidth: "110px",
                                        "& .MuiOutlinedInput-input": {
                                          fontSize: "0.85rem",
                                          textOverflow: "clip",
                                          overflow: "visible",
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              />
                            ) : (
                              row.task_no
                            )}
                          </TableCell>
                          <TableCell>
                            {isPlanEditMode ? (
                              <Tooltip
                                title={row.task_name || ""}
                                arrow
                                slotProps={tooltipStyleProps}
                              >
                                <Autocomplete
                                  size="small"
                                  options={projectTaskList.filter(
                                    (task) =>
                                      !formData.planned_tasks_today.some(
                                        (selected) =>
                                          selected.task_id === task.task_id,
                                      ) || task.task_id === row.task_id,
                                  )}
                                  getOptionLabel={(option) =>
                                    option.task_name || ""
                                  }
                                  value={
                                    projectTaskList.find(
                                      (t) => t.task_id === row.task_id,
                                    ) || null
                                  }
                                  onChange={(e, val) => {
                                    if (val) {
                                      const updatedList = [
                                        ...formData.planned_tasks_today,
                                      ];
                                      updatedList[index] = {
                                        ...updatedList[index],
                                        task_id: val.task_id,
                                        task_no: val.task_no,
                                        task_name: val.task_name,
                                        remaining_eta: val.remaining_eta,
                                      };
                                      setFormData({
                                        ...formData,
                                        planned_tasks_today: updatedList,
                                      });
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select"
                                      sx={styles.selectInput}
                                    />
                                  )}
                                />
                              </Tooltip>
                            ) : (
                              row.task_name
                            )}
                          </TableCell>
                          <TableCell>
                            {isPlanEditMode ? (
                              <TimePicker
                                value={
                                  row.planned_hours
                                    ? dayjs(row.planned_hours, "HH:mm")
                                    : null
                                }
                                onChange={(newValue) => {
                                  const timeStr = newValue
                                    ? newValue.format("HH:mm")
                                    : "";
                                  const inputMinutes = timeToMinutes(timeStr);
                                  const etaMinutes = row.remaining_eta
                                    ? timeToMinutes(row.remaining_eta)
                                    : Infinity;
                                  if (
                                    row.remaining_eta &&
                                    inputMinutes > etaMinutes
                                  ) {
                                    showToast(
                                      "error",
                                      `Cannot exceed Remaining ETA (${row.remaining_eta}). Resetting to max.`,
                                    );
                                    updatePlanRow(
                                      index,
                                      "planned_hours",
                                      row.remaining_eta,
                                    );
                                  } else {
                                    updatePlanRow(
                                      index,
                                      "planned_hours",
                                      timeStr,
                                    );
                                  }
                                }}
                                ampm={false}
                                disabled={!row.task_id}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    sx: { width: 110 },
                                  },
                                }}
                              />
                            ) : (
                              <Typography variant="body2">
                                {row.planned_hours
                                  ? row.planned_hours.substring(0, 5)
                                  : "-"}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{row.remaining_eta || "-"}</TableCell>
                          {isPlanEditMode && (
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removePlanRow(index)}
                              >
                                <RemoveCircleOutline fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={isPlanEditMode ? 5 : 4}
                          align="center"
                          sx={{
                            py: 3,
                            fontStyle: "italic",
                            color: "text.secondary",
                          }}
                        >
                          No planned tasks found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box>
                {formData.planned_tasks_today.length > 0 ? (
                  formData.planned_tasks_today.map((task, i) => (
                    <Box key={i} sx={styles.manualBox("planned")}>
                      {isPlanEditMode ? (
                        <TextField
                          fullWidth
                          value={task.task_name}
                          onChange={(e) =>
                            updatePlanRow(i, "task_name", e.target.value)
                          }
                          sx={styles.boxInput}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ px: 2, py: 1, fontWeight: 500 }}
                        >
                          {task.task_name}
                        </Typography>
                      )}
                      {isPlanEditMode && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removePlanRow(i)}
                        >
                          <RemoveCircleOutline />
                        </IconButton>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No planned tasks found
                  </Typography>
                )}
              </Box>
            )}
            {isPlanEditMode && (
              <Box sx={{ mt: 1 }}>
                <IconButton onClick={addPlanRow} sx={{ color: "#FFD700" }}>
                  <AddCircleOutline />
                </IconButton>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Achieved Tasks
              </Typography>
              {isEditMode && (
                <Tooltip
                  title="Auto-populate from server"
                  arrow
                  placement="top"
                >
                  <IconButton
                    size="small"
                    onClick={handleRefreshAchievedTasks}
                    sx={{ color: themes.primaryButton }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              List down the tasks you actually completed today.
            </Typography>
            {hasProjectAccess ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                <Table size="small">
                  <TableHead sx={styles.tableHead("#F1FDF9")}>
                    <TableRow>
                      <TableCell width="15%">Task No</TableCell>
                      <TableCell width="65%">Task Name</TableCell>
                      <TableCell width="20%">Worked Hrs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.achieved_tasks.length > 0 ? (
                      formData.achieved_tasks.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.task_no}</TableCell>
                          <TableCell>{row.task_name}</TableCell>
                          <TableCell>
                            {row.worked_hours.substring(0, 5)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{
                            py: 3,
                            fontStyle: "italic",
                            color: "text.secondary",
                          }}
                        >
                          No achieved tasks found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box>
                {formData.achieved_tasks.map((row, index) => (
                  <Box key={index} sx={styles.manualBox("achieved")}>
                    {isEditMode ? (
                      <TextField
                        fullWidth
                        value={row.task_name}
                        onChange={(e) =>
                          updateRow(
                            "achieved_tasks",
                            index,
                            "task_name",
                            e.target.value,
                          )
                        }
                        sx={styles.boxInput}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                        {row.task_name}
                      </Typography>
                    )}
                    {isEditMode && index > 0 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRow("achieved_tasks", index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {isEditMode &&
                  canAddNextRow(formData.achieved_tasks, "manual_achieved") && (
                    <IconButton
                      onClick={() => addRow("achieved_tasks")}
                      sx={{ color: "#00D4AA" }}
                    >
                      <AddCircleOutline />
                    </IconButton>
                  )}
                {!isEditMode && formData.achieved_tasks.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No achieved tasks found
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              mb: 4,
              opacity: isPlanEditMode ? 0.5 : 1,
              pointerEvents: isPlanEditMode ? "none" : "auto",
              transition: "opacity 0.3s ease",
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">
              Planned for Next Session
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              Add the tasks you intend to work on in the next session.
            </Typography>
            {hasProjectAccess ? (
              <Box>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  <Table size="small">
                    <TableHead sx={styles.tableHead("#FFF9E6")}>
                      <TableRow>
                        <TableCell width="15%">Task No</TableCell>
                        <TableCell width="50%">Task Name</TableCell>
                        <TableCell width="15%">Remaining ETA</TableCell>
                        <TableCell width="15%">Planned</TableCell>
                        {isEditMode && <TableCell width="5%"></TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.next_session_tasks.length > 0 ? (
                        formData.next_session_tasks.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {isEditMode ? (
                                <Tooltip
                                  title={row.task_no || ""}
                                  arrow
                                  slotProps={tooltipStyleProps}
                                >
                                  <Autocomplete
                                    size="small"
                                    options={projectTaskList.filter(
                                      (task) =>
                                        !formData.next_session_tasks.some(
                                          (selected) =>
                                            selected.task_id === task.task_id,
                                        ) || task.task_id === row.task_id,
                                    )}
                                    getOptionLabel={(option) =>
                                      option.task_no || ""
                                    }
                                    value={
                                      projectTaskList.find(
                                        (t) => t.task_id === row.task_id,
                                      ) || null
                                    }
                                    onChange={(e, val) => {
                                      const updatedList = [
                                        ...formData.next_session_tasks,
                                      ];
                                      if (val) {
                                        updatedList[index] = {
                                          ...updatedList[index],
                                          ...val,
                                        };
                                      } else {
                                        updatedList[index] = {
                                          ...updatedList[index],
                                          task_id: null,
                                          task_no: "",
                                          task_name: "",
                                          remaining_eta: "",
                                        };
                                      }
                                      setFormData({
                                        ...formData,
                                        next_session_tasks: updatedList,
                                      });
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="Select No"
                                        sx={styles.selectInput}
                                      />
                                    )}
                                  />
                                </Tooltip>
                              ) : (
                                row.task_no || "-"
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditMode ? (
                                <Tooltip
                                  title={row.task_name || ""}
                                  arrow
                                  slotProps={tooltipStyleProps}
                                >
                                  <Autocomplete
                                    size="small"
                                    options={projectTaskList.filter(
                                      (task) =>
                                        !formData.next_session_tasks.some(
                                          (selected) =>
                                            selected.task_id === task.task_id,
                                        ) || task.task_id === row.task_id,
                                    )}
                                    getOptionLabel={(option) =>
                                      option.task_name || ""
                                    }
                                    value={
                                      projectTaskList.find(
                                        (t) => t.task_id === row.task_id,
                                      ) || null
                                    }
                                    onChange={(e, val) => {
                                      if (val) {
                                        const updatedList = [
                                          ...formData.next_session_tasks,
                                        ];
                                        updatedList[index] = {
                                          ...updatedList[index],
                                          ...val,
                                        };
                                        setFormData({
                                          ...formData,
                                          next_session_tasks: updatedList,
                                        });
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="Select"
                                        sx={styles.selectInput}
                                      />
                                    )}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography variant="body2">
                                  {row.task_name}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{row.remaining_eta || "-"}</TableCell>
                            <TableCell>
                              {isEditMode ? (
                                <TimePicker
                                  value={
                                    row.planned_hours
                                      ? dayjs(row.planned_hours, "HH:mm")
                                      : null
                                  }
                                  onChange={(newValue) => {
                                    const timeStr = newValue
                                      ? newValue.format("HH:mm")
                                      : "";
                                    const inputMinutes = timeToMinutes(timeStr);
                                    const etaMinutes = row.remaining_eta
                                      ? timeToMinutes(row.remaining_eta)
                                      : Infinity;
                                    if (
                                      row.remaining_eta &&
                                      inputMinutes > etaMinutes
                                    ) {
                                      showToast(
                                        "error",
                                        `Cannot exceed Remaining ETA (${row.remaining_eta}). Resetting to max.`,
                                      );
                                      updateRow(
                                        "next_session_tasks",
                                        index,
                                        "planned_hours",
                                        row.remaining_eta,
                                      );
                                    } else {
                                      updateRow(
                                        "next_session_tasks",
                                        index,
                                        "planned_hours",
                                        timeStr,
                                      );
                                    }
                                  }}
                                  ampm={false}
                                  disabled={!row.task_id}
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      sx: { width: 110 },
                                      error:
                                        row.remaining_eta &&
                                        timeToMinutes(row.planned_hours) >
                                          timeToMinutes(row.remaining_eta),
                                    },
                                  }}
                                />
                              ) : (
                                <Typography variant="body2">
                                  {row.planned_hours
                                    ? row.planned_hours.substring(0, 5)
                                    : "-"}
                                </Typography>
                              )}
                            </TableCell>
                            {isEditMode && (
                              <TableCell>
                                {index > 0 && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      removeRow("next_session_tasks", index)
                                    }
                                  >
                                    <RemoveCircleOutline fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={isEditMode ? 5 : 4}
                            align="center"
                            sx={{
                              py: 3,
                              fontStyle: "italic",
                              color: "text.secondary",
                            }}
                          >
                            No tasks planned for next session
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {isEditMode &&
                  canAddNextRow(
                    formData.next_session_tasks,
                    "project_planned",
                  ) && (
                    <IconButton
                      onClick={() => addRow("next_session_tasks")}
                      sx={{ mt: 1, color: "#FFD700" }}
                    >
                      <AddCircleOutline />
                    </IconButton>
                  )}
              </Box>
            ) : (
              <Box>
                {formData.next_session_tasks.map((row, index) => (
                  <Box key={index} sx={styles.manualBox("planned")}>
                    {isEditMode ? (
                      <TextField
                        fullWidth
                        value={row.task_name}
                        onChange={(e) =>
                          updateRow(
                            "next_session_tasks",
                            index,
                            "task_name",
                            e.target.value,
                          )
                        }
                        sx={styles.boxInput}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ px: 2, py: 1 }}>
                        {row.task_name}
                      </Typography>
                    )}
                    {isEditMode && index > 0 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRow("next_session_tasks", index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {isEditMode &&
                  canAddNextRow(
                    formData.next_session_tasks,
                    "manual_planned",
                  ) && (
                    <IconButton
                      onClick={() => addRow("next_session_tasks")}
                      sx={{ color: "#FFD700" }}
                    >
                      <AddCircleOutline />
                    </IconButton>
                  )}
                {!isEditMode && formData.next_session_tasks.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    No tasks planned for next session
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Blockers / Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={isEditMode ? "Add blockers..." : "No notes"}
              value={formData.blockers_notes}
              onChange={(e) => handleChange("blockers_notes", e.target.value)}
              InputProps={{ readOnly: !isEditMode }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: isEditMode ? "#fff" : "#f9f9f9",
                },
              }}
            />
          </Box>
          {reportData?.status === "Reopened" && !isEditMode && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Reopened Reason
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#fff8f8",
                  borderColor: "#ffcdd2",
                }}
              >
                {formatReopenHistory(reportData?.approver_comments).map(
                  (entry, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        mb: 1,
                        pb: 1,
                        borderBottom:
                          index !==
                          formatReopenHistory(reportData?.approver_comments)
                            .length -
                            1
                            ? "1px dashed #ddd"
                            : "none",
                      }}
                    >
                      {entry}
                    </Typography>
                  ),
                )}
              </Paper>
            </Box>
          )}

          {isTeamMode && (
            <Paper
              variant="outlined"
              sx={{ p: 2, mt: 4, bgcolor: "#f9f9f9", borderColor: "#ddd" }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Manager Actions
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" display="block">
                    Rating (1-5)
                  </Typography>
                  <Rating
                    value={managerInputs.rating}
                    onChange={(e, val) =>
                      setManagerInputs({ ...managerInputs, rating: val })
                    }
                    disabled={!isSubmitted || showReopenField}
                  />
                </Box>
                <TextField
                  label="Approver Comments"
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={managerInputs.comments}
                  onChange={(e) =>
                    setManagerInputs({
                      ...managerInputs,
                      comments: e.target.value,
                    })
                  }
                  disabled={
                    !isSubmitted ||
                    reportData?.status === "Approved" ||
                    showReopenField
                  }
                  sx={{ bgcolor: "#fff" }}
                />
              </Box>
              {showReopenField && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="error" fontWeight="bold">
                    Reason for Reopening:
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Explain why this report is being reopened..."
                    value={managerInputs.reopenReason}
                    onChange={(e) =>
                      setManagerInputs({
                        ...managerInputs,
                        reopenReason: e.target.value,
                      })
                    }
                    sx={{ mt: 1, bgcolor: "#fff" }}
                  />
                </Box>
              )}
            </Paper>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #eee",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Box>
            {!isTeamMode && !isEditMode && !isPlanEditMode && (
              <Tooltip
                title={
                  autoPopulateFailed
                    ? "Unable to auto-populate tasks - submit disabled"
                    : isSubmitted
                      ? `${reportData?.status || "Submitted"} - cannot submit`
                      : isWorkedHoursInvalid
                        ? "Worked hours exceed 17:00"
                        : ""
                }
                arrow
                slotProps={tooltipStyleProps}
              >
                <span>
                  <CustomButton
                    actionFuntion={handleSubmitReport}
                    actionName="Submit"
                    typeName="button"
                    loading={submitting}
                    disableAction={
                      submitting ||
                      isSubmitted ||
                      isWorkedHoursInvalid ||
                      autoPopulateFailed
                    }
                    customTitleSx={{
                      bgcolor: "#00C853",
                      "&:hover": { bgcolor: "#00E676" },
                      px: 3,
                    }}
                  />
                </span>
              </Tooltip>
            )}
          </Box>
          {isPlanEditMode && (
            <>
              <CustomButtonWithOutLoader
                actionFuntion={handlePlanEditToggle}
                actionName="Cancel"
                typeName="button"
                customTitleSx={{
                  color: "#666",
                  background: "transparent",
                  boxShadow: "none",
                }}
              />
              <CustomButton
                actionFuntion={handleSaveCurrentPlan}
                actionName="Save Plan"
                typeName="button"
                loading={planSaveLoading}
                disableAction={
                  isPlanSaveDisabled() ||
                  formData.planned_tasks_today.length === 0 ||
                  planSaveLoading
                }
                customTitleSx={{
                  bgcolor: "#00C853",
                  "&:hover": { bgcolor: "#00E676" },
                  px: 3,
                }}
              />
            </>
          )}
          {!isTeamMode && isEditMode && !isPlanEditMode && (
            <>
              <CustomButtonWithOutLoader
                actionFuntion={handleEditToggle}
                actionName="Cancel"
                typeName="button"
                customTitleSx={{
                  color: "#666",
                  background: "transparent",
                  boxShadow: "none",
                }}
              />
              <CustomButton
                actionFuntion={handleUpdate}
                actionName="Update"
                typeName="button"
                loading={loading}
                disableAction={isSaveDisabled() || loading}
                customTitleSx={{
                  bgcolor: "#00C853",
                  "&:hover": { bgcolor: "#00E676" },
                  px: 3,
                }}
              />
            </>
          )}
          {isTeamMode && (
            <>
              {!showReopenField ? (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowReopenField(true)}
                    disabled={
                      actionLoading || reportData?.status === "Reopened"
                    }
                  >
                    Reopen
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleApprove}
                    disabled={
                      !isSubmitted ||
                      reportData?.status === "Approved" ||
                      reportData?.status === "Missed" ||
                      actionLoading
                    }
                    sx={{ color: "#fff", fontWeight: "bold" }}
                  >
                    Approve
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => setShowReopenField(false)}
                    disabled={actionLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleReopen}
                    disabled={actionLoading}
                  >
                    Confirm Reopen
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default EODViewPanel;
